import { message } from 'antd';

import { CreateTiveShipment, GetTiveShipmentAccess } from '../../apis/shipment';
import type { SensorFormRow, SensorSelectRow } from './sensorFormPayload';

export const TIVE_ADDRESS_REQUIRED_MESSAGE =
  'Pickup and delivery are required to create the shipment in Tive.';

/** The Tive sensor this company may auto-create for, or '' when it may not.
 *
 * Fails closed: an unreachable gate hides the checkbox rather than offering an
 * action that cannot work.
 */
export const fetchTiveAutocreateSensorId = (token: string): Promise<string> =>
  GetTiveShipmentAccess(token)
    .then((res): string =>
      res?.data?.status === 'active' ? String(res.data.sensor_id ?? '') : ''
    )
    .catch((): string => '');

type Coordinates = {
  latitude?: number | null;
  longitude?: number | null;
} | null;

/** Whether the location in effect is geocoded; Tive rejects a create without coordinates.
 *
 * `picked` wins whenever the user has chosen a location in this session, even when it
 * has no coordinates -- falling through to `saved` would pass the check and then fail
 * server-side on the address the save actually wrote.
 */
export const hasCoordinates = (
  picked: Coordinates | undefined,
  saved?: Coordinates | undefined
): boolean => {
  const effective = picked ?? saved;
  const { latitude, longitude } = effective ?? {};
  if (latitude == null || longitude == null) {
    return false;
  }
  // get_location_details() returns lat/lng 0 for a shipment with no location,
  // so an unset origin would otherwise pass this check and fail server-side.
  return !(latitude === 0 && longitude === 0);
};

/** Tracking ids already carrying a Tive shipment id, which must not be re-created. */
export const linkedTrackingIds = (
  sensors:
    | Array<{
        sensor_tracking_id?: string;
        sensor_manufacturer_shipment_id?: string | null;
      }>
    | undefined
): Set<string> =>
  new Set(
    (sensors ?? [])
      .filter(
        (s) => (s.sensor_manufacturer_shipment_id ?? '').trim().length > 0
      )
      .map((s) => String(s.sensor_tracking_id ?? '').trim())
  );

/** Whether a sensor row needs a Tive shipment this company is able to create. */
export const hasEligibleTiveRow = (
  rows: SensorFormRow[] | undefined,
  sensorsList: SensorSelectRow[],
  eligibleSensorId: string,
  alreadyLinked: Set<string> = new Set()
): boolean => {
  if (!rows?.length || !eligibleSensorId) {
    return false;
  }
  return rows.some((row) => {
    const selected = row?.sensor;
    if (!selected) {
      return false;
    }
    if (alreadyLinked.has(String(row?.sensor_tracking_id ?? '').trim())) {
      return false;
    }
    const match = sensorsList.find(
      (s) =>
        String(s.id) === String(selected) ||
        String(s.sensor_id) === String(selected)
    );
    return String(match?.sensor_id ?? '') === String(eligibleSensorId);
  });
};

/** Run the create and report the outcome; never throws, the save already succeeded. */
export const runTiveAutocreate = (
  token: string,
  shipmentId: string
): Promise<boolean> =>
  CreateTiveShipment(token, shipmentId)
    .then((res) => {
      const failed = res?.data?.devices_failed ?? [];
      if (failed.length > 0) {
        message.warning(
          `Tive shipment ${res.data.public_shipment_id} created, but these trackers were not found in Tive: ${failed.join(', ')}.`
        );
      } else {
        message.success(
          `Tive shipment ${res.data.public_shipment_id} created.`
        );
      }
      return true;
    })
    .catch((error) => {
      const detail =
        error?.data?.detail ??
        'Could not create the shipment in Tive. You can retry from the shipment.';
      message.warning(detail);
      return false;
    });
